import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import Label, { LabelProps } from '~/components/Label/Label';
import {
  checkedDisabledIcon,
  checkedHoverIcon,
  checkedIcon,
  partiallyCheckedDisabledIcon,
  partiallyCheckedHoverIcon,
  partiallyCheckedIcon,
  uncheckedDisabledIcon,
  uncheckedHoverIcon,
  uncheckedIcon,
} from '~/constants/icons';
import * as React from 'react';
import { ReactNode } from 'react';
import { WrappedFieldProps } from 'redux-form';
import { EventHandler } from 'redux-form/lib/Field';
import { colors } from '~/styles/constants';

const Color = require('color');

type CheckboxLabelPosition = 'left' | 'right';
export type CheckedTypes = boolean | 'partial';

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
`;

interface BoxProps {
  disabled?: boolean;
  invalid?: boolean;
  checked?: CheckedTypes;
  focused?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (evt: any) => void;
}

interface BoxContainerProps {
  customStyles?: string;
  showBackgroundColor?: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-types
const BoxContainer = styled('div')<BoxContainerProps>`
  display: flex;
  align-items: center;
  ${(props) => (props.customStyles ? `${props.customStyles};` : '')}
  ${(props) =>
    props.showBackgroundColor
      ? `background-color: ${colors.forest700}`
      : `background-color: ${colors.white}`};
  ${(props) => props.showBackgroundColor && `border: 2px solid ${colors.forest700}`};
`;

export const Box = styled('span')<BoxProps>`
  position: relative;
  height: 14px;
  width: 14px;
  line-height: 14px;
  font-size: 10px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
  flex-shrink: 0;
`;

const SLIDER_SIZE = {
  lg: 20,
  md: 15,
  sm: 10,
};

const SLIDER_HORIZONTAL_SPACING = {
  lg: 5,
  md: 4,
  sm: 3,
};

type ToggleSize = 'sm' | 'md' | 'lg';

type ToggleTheme = {
  width: number;
  height: number;
  sliderSize: number;
  sliderHorizontalSpacing: number;
  sliderVerticalSpacing: number;
  transform: string;
  backgroundColor: string;
};

const disabledPrimaryColor = Color(colors.primary).lighten(0.4).hex();

const disabledSecondaryColor = Color(colors.secondary).lighten(0.6).hex();

// eslint-disable-next-line @typescript-eslint/ban-types
const Toggle = styled('label')<{
  theme?: ToggleTheme;
  disabled?: boolean;
  children?: ReactNode;
}>`
  position: relative;
  display: inline-block;
  width: ${(props) => props.theme.width}px;
  height: ${(props) => props.theme.height}px;
  min-width: ${(props) => props.theme.width}px;
`;

const Slider = styled('span')<{ theme?: ToggleTheme; disabled?: boolean; focused?: boolean }>`
  position: absolute;
  border-radius: ${(props) => props.theme.height}px;
  cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => props.theme.backgroundColor};
  transition: 0.4s;
  min-width: ${(props) => props.theme.sliderSize}px;
  z-index: 2;

  ::before {
    position: absolute;
    content: '';
    border-radius: 50%;
    height: ${(props) => props.theme.sliderSize}px;
    width: ${(props) => props.theme.sliderSize}px;
    bottom: ${(props) => props.theme.sliderVerticalSpacing}px;
    left: ${(props) => props.theme.sliderHorizontalSpacing}px;
    background-color: ${colors.white};
    transition: 0.4s;
    ${({ theme: { transform } }) => `transform: ${transform}`};
  }
`;

const InvisibleCheckbox = styled('input')`
  opacity: 0;
  height: 0;
  width: 0;
  margin: 0;
  position: absolute;
  z-index: 1;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

const ErrorMessage = styled('div')`
  margin-top: 5px;
  font-size: 12px;
  color: ${colors.dangerRed};
`;

const getIcon = (
  checked: CheckedTypes | undefined,
  disabled: boolean,
  focused: boolean,
  circle: boolean,
) => {
  switch (checked) {
    case true:
      if (disabled) return checkedDisabledIcon;
      if (focused) return checkedHoverIcon;
      if (circle) return null;
      return checkedIcon;
    case 'partial':
      if (disabled) return partiallyCheckedDisabledIcon;
      if (focused) return partiallyCheckedHoverIcon;
      return partiallyCheckedIcon;
    default:
      if (disabled) return uncheckedDisabledIcon;
      if (focused) return uncheckedHoverIcon;
      if (circle) return null;
      return uncheckedIcon;
  }
};

const guidGenerator = () => {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `${S4() + S4()}'-'${S4()}-${S4()}-${S4()}-${S4() + S4() + S4()}`;
};

export interface CheckboxProps {
  label?: string | JSX.Element;
  disabled?: boolean;
  checked?: CheckedTypes;
  className?: string;
  id?: string;
  focused?: boolean;
  onChange?(value: CheckedTypes): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFocus?: EventHandler<React.FocusEvent<any>>;
  onBlur?(value: CheckedTypes): void;
  tabIndex?: number;
  dataAnalytics?: string;
  dataPendo?: string;
  dataE2E?: string;
  dataTestid?: string;
  toggle?: boolean;
  labelPosition?: CheckboxLabelPosition;
  labelProps?: LabelProps;
  toggleSize?: ToggleSize;
  hideErrors?: boolean;
  errorMessage?: string;
  customBoxStyles?: string;
  circle?: boolean;
}

const Checkbox = (props: CheckboxProps) => {
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { disabled, onChange } = props;

    if (disabled) return;

    if (onChange) {
      onChange(!props.checked);
    }
  };

  const onInvisibleCheckboxBlur = () => {
    const { checked, onBlur } = props;

    if (onBlur) {
      onBlur(checked!);
    }
  };

  const renderErrorMessage = () => {
    const { errorMessage } = props;

    if (!errorMessage) return null;

    return <ErrorMessage>{errorMessage}</ErrorMessage>;
  };

  const getToggleThemeFromProps = (): ToggleTheme => {
    const toggleSize = props.toggleSize || 'lg';
    const sliderSize = SLIDER_SIZE[toggleSize];
    const horizontalSpacing = SLIDER_HORIZONTAL_SPACING[toggleSize];
    const width = (sliderSize + horizontalSpacing) * 2;
    const height = sliderSize + (horizontalSpacing - 1) * 2;
    const sliderTransition = width - sliderSize - horizontalSpacing * 2;

    const transform = props.checked ? `translateX(${sliderTransition}px)` : '';
    const backgroundColor = props.checked
      ? props.disabled
        ? disabledPrimaryColor
        : colors.primary
      : props.disabled
        ? disabledSecondaryColor
        : colors.secondary;

    return {
      width,
      height,
      sliderSize,
      backgroundColor,
      transform,
      sliderHorizontalSpacing: horizontalSpacing,
      sliderVerticalSpacing: horizontalSpacing - 1,
    };
  };

  let checkIcon: Maybe<JSX.Element> = null;
  let labelComponent: Maybe<JSX.Element> = null;
  const randomId = guidGenerator();
  const {
    disabled,
    label,
    className,
    id,
    checked,
    focused,
    tabIndex,
    dataAnalytics,
    dataPendo,
    dataE2E,
    dataTestid,
    toggle,
    labelPosition,
    labelProps,
    errorMessage,
    customBoxStyles,
    circle,
  } = props;

  const iconSrc = getIcon(checked, !!disabled, !!focused, !!circle);

  checkIcon = iconSrc ? (
    <img
      src={iconSrc}
      data-analytics={dataAnalytics && `${dataAnalytics}Image`}
      alt={checked ? 'Checked box' : 'Unchecked box'}
    />
  ) : (
    <span
      data-analytics={dataAnalytics && `${dataAnalytics}Image`}
      aria-label={checked ? 'Checked box' : 'Unchecked box'}
    />
  );

  if (label) {
    labelComponent = (
      <Label
        {...labelProps}
        disabled={disabled}
        labelPosition={labelPosition || 'right'}
        data-pendo={dataPendo}
        data-e2e={dataE2E}
        dataAnalytics={dataAnalytics}
        htmlFor={id || randomId}
      >
        {label}
      </Label>
    );
  }

  const invisibleCheckbox = (
    <InvisibleCheckbox
      type="checkbox"
      disabled={disabled}
      onFocus={props.onFocus}
      onBlur={onInvisibleCheckboxBlur}
      data-analytics={dataAnalytics && `${dataAnalytics}Input`}
      tabIndex={tabIndex}
      id={id || randomId}
    />
  );

  let checkBoxContent = (
    <BoxContainer
      customStyles={customBoxStyles}
      showBackgroundColor={props.circle && props.checked === true}
    >
      {toggle ? (
        <ThemeProvider theme={getToggleThemeFromProps()}>
          <Toggle>
            <Slider
              disabled={disabled}
              onClick={onClick}
              focused={focused}
              data-pendo={dataPendo}
              data-e2e={dataE2E}
              data-testid={dataTestid}
            />
            {invisibleCheckbox}
          </Toggle>
        </ThemeProvider>
      ) : (
        <Box
          disabled={disabled}
          checked={checked}
          onClick={onClick}
          focused={focused}
          data-pendo={dataPendo}
          data-e2e={dataE2E}
          data-testid={dataTestid}
          invalid={!!errorMessage}
        >
          {checkIcon}
          {invisibleCheckbox}
        </Box>
      )}
    </BoxContainer>
  );

  // Add Label based on specified position (default is right side)
  checkBoxContent =
    labelPosition === 'left' ? (
      <React.Fragment>
        {labelComponent}
        {checkBoxContent}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {checkBoxContent}
        {labelComponent}
      </React.Fragment>
    );

  return (
    <React.Fragment>
      <CheckboxContainer id={id ? `${id}-container` : undefined} className={className}>
        {checkBoxContent}
      </CheckboxContainer>
      {renderErrorMessage()}
    </React.Fragment>
  );
};

export const CheckboxField = (props: CheckboxProps & WrappedFieldProps) => {
  const { input, meta, hideErrors, ...checkboxProps } = props;

  const errorMessage = !hideErrors && meta && meta.touched && meta.error ? meta.error : null;

  return (
    <Checkbox
      checked={input.value}
      onChange={input.onChange}
      onFocus={input.onFocus}
      onBlur={input.onBlur}
      focused={meta.active}
      data-pendo={checkboxProps.dataPendo}
      dataAnalytics={checkboxProps.dataAnalytics}
      data-e2e={checkboxProps.dataE2E}
      data-testid={checkboxProps.dataTestid}
      errorMessage={errorMessage}
      {...checkboxProps}
    />
  );
};

export default Checkbox;
